/**
 * Kickard PHASE 4.2.1 — Competitive Result block.
 *
 * Read-only summary of what a ranked match did to the player's
 * competitive progress. Renders inline on the post-match screen so the
 * player never has to open Luck Report to see SR gain/loss.
 *
 * Data sources (all authoritative, server-computed):
 *   • Ranked SR delta, current SR, tier before/after, placement state
 *     → GET /api/ranked/result/{matchId}  (rankedClient.getRankedResult)
 *   • Weekly Cup contribution + X/15 progress
 *     → GET /api/ranked/weekly-cup/match/{matchId}
 *       (weeklyCupClient.fetchWeeklyCupMatchContribution)
 *
 * No SR / MMR / Weekly-Cup formula lives here. No writes. Renders
 * NOTHING when matchType !== 'ranked' or matchId / guestId are missing.
 */
import React, { useEffect, useState } from 'react';
import { getRankedResult } from '@/api/rankedClient';
import { fetchWeeklyCupMatchContribution } from '@/api/weeklyCupClient';

const MAX_RANKED_POLLS = 12;    // ~ 12 s total budget for pending result
const RANKED_POLL_INTERVAL_MS = 1000;

const TIER_ORDER = {
  unranked: 0, bronze: 1, silver: 2, gold: 3, platinum: 4,
  diamond: 5, master: 6, champion: 7, legend: 8,
};

const ROMAN = { 1: 'I', 2: 'II', 3: 'III' };

function tierLabel(tier, division) {
  if (!tier || tier === 'unranked') return 'UNRANKED';
  if (tier === 'legend') return 'LEGEND';
  const name = tier.toUpperCase();
  const div = ROMAN[Number(division) || 0];
  return div ? `${name} ${div}` : name;
}

function pointsSuffix(points) {
  // "+1 PT" vs "+3 PTS" — grammar polish.
  return Math.abs(Number(points || 0)) === 1 ? 'PT' : 'PTS';
}

export default function CompetitiveResultBlock({ matchId, matchType, myGuestId }) {
  // Gate: this block is ranked-only. Local / friend / quick / bot never
  // render it. Also require both matchId + guestId — without either
  // there's nothing authoritative to fetch.
  const eligible = matchType === 'ranked' && !!matchId && !!myGuestId;

  const [rankedResult, setRankedResult] = useState(null);
  const [weeklyContribution, setWeeklyContribution] = useState(null);
  const [rankedTries, setRankedTries] = useState(0);
  const [weeklyTries, setWeeklyTries] = useState(0);
  // PHASE 4.2.1A — manual retry cycles let the user re-arm both fetch
  // effects after the automatic poll budget is exhausted, without ever
  // re-processing the match server-side. Bumping `retryCycle` resets
  // both counters + re-triggers both effects.
  const [retryCycle, setRetryCycle] = useState(0);

  // Fetch ranked result with light retry while pending.
  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    (async () => {
      const r = await getRankedResult(matchId, myGuestId);
      if (cancelled) return;
      setRankedResult(r);
      if (r?.ok && r.status === 'pending' && rankedTries < MAX_RANKED_POLLS) {
        setTimeout(() => {
          if (!cancelled) setRankedTries((n) => n + 1);
        }, RANKED_POLL_INTERVAL_MS);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, matchId, myGuestId, rankedTries, retryCycle]);

  // Fetch Weekly Cup contribution (also polls while ranked result is
  // pending, because the match_history / weekly-cup row is written on
  // the same code path and can be a beat behind).
  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    (async () => {
      const r = await fetchWeeklyCupMatchContribution(matchId, myGuestId);
      if (cancelled) return;
      setWeeklyContribution(r);
      // Retry a few times while the match is still propagating.
      if (r?.ok && r.matchFound === false && weeklyTries < MAX_RANKED_POLLS) {
        setTimeout(() => {
          if (!cancelled) setWeeklyTries((n) => n + 1);
        }, RANKED_POLL_INTERVAL_MS);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, matchId, myGuestId, weeklyTries, retryCycle]);

  if (!eligible) return null;

  // Have we exhausted the automatic poll budget on either side without
  // authoritative data? If so, offer a manual retry instead of the
  // silent infinite spinner.
  const rankedExhausted =
    rankedTries >= MAX_RANKED_POLLS
    && rankedResult?.ok && rankedResult.status === 'pending';
  const weeklyExhausted =
    weeklyTries >= MAX_RANKED_POLLS
    && weeklyContribution?.ok && weeklyContribution.matchFound === false;

  const handleRetry = () => {
    setRankedTries(0);
    setWeeklyTries(0);
    setRankedResult(null);
    setWeeklyContribution(null);
    setRetryCycle((n) => n + 1);
  };

  return (
    <div
      data-testid="competitive-result-block"
      className="mt-6 w-full max-w-[340px] bg-[hsl(var(--k-bg-elev)/0.85)] border border-[hsl(var(--k-line))] rounded-xl overflow-hidden"
    >
      <RankedSection
        result={rankedResult}
        exhausted={rankedExhausted}
        onRetry={handleRetry}
      />
      <div className="h-px bg-white/10 mx-5" />
      <WeeklyCupSection
        contribution={weeklyContribution}
        exhausted={weeklyExhausted}
        onRetry={handleRetry}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
function RankedSection({ result, exhausted, onRetry }) {
  // Manual-retry state trumps any spinner — surfaced when the poll
  // budget was exhausted with the endpoint still returning pending.
  if (exhausted) {
    return (
      <SectionShell title="Ranked Result" testid="ranked-section-retry">
        <div className="text-white/55 text-xs text-center leading-relaxed">
          Result temporarily unavailable.
        </div>
        <button
          type="button"
          data-testid="competitive-result-retry"
          onClick={onRetry}
          className="mt-2 px-4 py-1.5 rounded-full text-xs font-heading tracking-[0.24em] uppercase border border-[hsl(var(--k-gold-bright))/0.4] text-[hsl(var(--k-gold-bright))] hover:bg-[hsl(var(--k-gold-bright))/0.08] transition"
        >
          Retry
        </button>
      </SectionShell>
    );
  }
  // Loading — result still fetching.
  if (result === null) {
    return (
      <SectionShell title="Ranked Result" testid="ranked-section-loading">
        <div className="text-white/45 text-xs">Loading…</div>
      </SectionShell>
    );
  }
  if (!result.ok) {
    // 403 (not participant) or network — hide silently. Non-ranked
    // matches never even reach this because of the outer gate, so 403
    // here is a legitimate error we don't want to surface.
    return (
      <SectionShell title="Ranked Result" testid="ranked-section-unavailable">
        <div className="text-white/45 text-xs">Ranked result unavailable.</div>
      </SectionShell>
    );
  }
  if (result.status === 'pending') {
    return (
      <SectionShell title="Ranked Result" testid="ranked-section-pending">
        <div className="text-white/60 text-xs">Result processing…</div>
      </SectionShell>
    );
  }
  if (result.status === 'ignored' || result.status === 'not_ranked') {
    return (
      <SectionShell title="Ranked Result" testid="ranked-section-ignored">
        <div className="text-white/45 text-xs">Ranked result unavailable.</div>
      </SectionShell>
    );
  }

  // Processed — authoritative fields.
  const p = result.player || {};
  const delta = Number(p.srDelta || 0);
  const isWin = delta > 0;
  const isLoss = delta < 0;
  const currentSR = Number(p.afterSR || 0);
  const beforeTier = p.beforeTier || 'unranked';
  const afterTier = p.afterTier || 'unranked';
  const afterDivision = p.afterDivision;
  const placementDone = !!p.placementComplete;
  const placementPlayed = Number(p.placementMatchesPlayed || 0);
  const placementReq = Number(p.placementMatchesRequired || 10);

  // Was placement JUST completed by this match?
  const placementJustCompleted =
    placementDone && beforeTier === 'unranked' && afterTier !== 'unranked';

  // Tier change AFTER placements — NOT the placement-completion event.
  const tierChanged =
    beforeTier !== afterTier
    && beforeTier !== 'unranked'
    && afterTier !== 'unranked';
  const isPromotion =
    tierChanged
    && (TIER_ORDER[afterTier] || 0) > (TIER_ORDER[beforeTier] || 0);
  const isDemotion = tierChanged && !isPromotion;

  const deltaColor = isWin
    ? 'text-[hsl(var(--k-gold-bright))]'
    : isLoss
      ? 'text-[hsl(var(--k-red))]'
      : 'text-white/70';
  const deltaText = isWin ? `+${delta} SR` : `${delta} SR`;

  return (
    <SectionShell title="Ranked Result" testid="ranked-section-processed">
      <div className="flex flex-col items-center">
        <div
          data-testid="ranked-sr-delta"
          className={`font-heading text-4xl tracking-[0.02em] ${deltaColor}`}
          style={{
            textShadow: isWin || isLoss ? `0 0 12px currentColor` : undefined,
          }}
        >
          {deltaText}
        </div>

        {/* Second line — depends on placement / tier-change context. */}
        {!placementDone && (
          <div
            data-testid="ranked-placement-progress"
            className="mt-3 flex flex-col items-center"
          >
            <div className="font-heading text-[10px] tracking-[0.32em] uppercase text-[hsl(var(--k-gold-bright))]/80">
              Placement
            </div>
            <div
              className="font-heading text-2xl text-white mt-1"
              data-testid="ranked-placement-count"
            >
              {placementPlayed} / {placementReq}
            </div>
          </div>
        )}

        {placementJustCompleted && (
          <div
            data-testid="ranked-placement-complete"
            className="mt-3 flex flex-col items-center"
          >
            <div className="font-heading text-[10px] tracking-[0.32em] uppercase text-[hsl(var(--k-gold-bright))]">
              Placement Complete
            </div>
            <div
              className="font-heading text-2xl text-white mt-1"
              data-testid="ranked-tier-label"
            >
              {tierLabel(afterTier, afterDivision)}
            </div>
            <div
              className="font-mono text-white/60 text-sm mt-0.5"
              data-testid="ranked-sr-value"
            >
              {currentSR.toLocaleString()} SR
            </div>
          </div>
        )}

        {placementDone && !placementJustCompleted && (isPromotion || isDemotion) && (
          <div
            data-testid={isPromotion ? 'ranked-promotion' : 'ranked-demotion'}
            className="mt-3 flex flex-col items-center"
          >
            <div
              className={`font-heading text-[10px] tracking-[0.32em] uppercase ${
                isPromotion
                  ? 'text-[hsl(var(--k-gold-bright))]'
                  : 'text-[hsl(var(--k-red))]'
              }`}
            >
              {isPromotion ? 'Promoted' : 'Demoted'}
            </div>
            <div className="font-heading text-lg text-white mt-1 tracking-[0.05em]">
              <span className="text-white/50">{tierLabel(beforeTier, null)}</span>
              <span className="mx-2 text-white/40">→</span>
              <span data-testid="ranked-tier-label">
                {tierLabel(afterTier, afterDivision)}
              </span>
            </div>
            <div
              className="font-mono text-white/60 text-sm mt-0.5"
              data-testid="ranked-sr-value"
            >
              {currentSR.toLocaleString()} SR
            </div>
          </div>
        )}

        {placementDone && !placementJustCompleted && !isPromotion && !isDemotion && (
          <div
            data-testid="ranked-tier-current"
            className="mt-3 flex flex-col items-center"
          >
            <div
              className="font-heading text-lg text-white tracking-[0.08em]"
              data-testid="ranked-tier-label"
            >
              {tierLabel(afterTier, afterDivision)}
            </div>
            <div
              className="font-mono text-white/60 text-sm mt-0.5"
              data-testid="ranked-sr-value"
            >
              {currentSR.toLocaleString()} SR
            </div>
          </div>
        )}
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
function WeeklyCupSection({ contribution, exhausted, onRetry }) {
  if (exhausted) {
    return (
      <SectionShell title="Weekly Cup" testid="weekly-section-retry">
        <div className="text-white/55 text-xs text-center leading-relaxed">
          Result temporarily unavailable.
        </div>
        <button
          type="button"
          data-testid="competitive-result-retry-weekly"
          onClick={onRetry}
          className="mt-2 px-4 py-1.5 rounded-full text-xs font-heading tracking-[0.24em] uppercase border border-[hsl(var(--k-gold-bright))/0.4] text-[hsl(var(--k-gold-bright))] hover:bg-[hsl(var(--k-gold-bright))/0.08] transition"
        >
          Retry
        </button>
      </SectionShell>
    );
  }
  if (contribution === null) {
    return (
      <SectionShell title="Weekly Cup" testid="weekly-section-loading">
        <div className="text-white/45 text-xs">Loading…</div>
      </SectionShell>
    );
  }
  if (!contribution.ok) {
    return (
      <SectionShell title="Weekly Cup" testid="weekly-section-unavailable">
        <div className="text-white/45 text-xs">Weekly Cup result unavailable.</div>
      </SectionShell>
    );
  }

  // Not a ranked match / not found — hide gracefully. Outer gate already
  // prevents non-ranked, but propagation delay can produce
  // `matchFound=false` briefly.
  if (contribution.matchFound === false || contribution.isRanked === false) {
    return (
      <SectionShell title="Weekly Cup" testid="weekly-section-pending">
        <div className="text-white/60 text-xs">Result processing…</div>
      </SectionShell>
    );
  }

  const counted = !!contribution.counted;
  const pts = Number(contribution.pointsEarned ?? 0);
  const countedMatches = Number(contribution.countedMatches ?? 0);
  const max = Number(contribution.maxCountedMatches ?? 15);
  const weekComplete = !!contribution.weekComplete;

  // Match counted → show +N PTS + X/15.
  if (counted) {
    return (
      <SectionShell title="Weekly Cup" testid="weekly-section-counted">
        <div className="flex flex-col items-center">
          <div
            data-testid="weekly-points"
            className="font-heading text-3xl text-[hsl(var(--k-gold-bright))]"
            style={{ textShadow: '0 0 10px hsl(var(--k-gold-bright) / 0.55)' }}
          >
            +{pts} {pointsSuffix(pts)}
          </div>
          <div
            data-testid="weekly-progress"
            className="font-mono text-white/70 text-sm mt-2 tracking-[0.15em]"
          >
            {countedMatches} / {max}
          </div>
          {weekComplete && (
            <div
              data-testid="weekly-complete-banner"
              className="mt-3 text-center"
            >
              <div className="font-heading text-[11px] tracking-[0.32em] uppercase text-[hsl(var(--k-gold-bright))]">
                Weekly Cup Complete
              </div>
              <div className="text-white/50 text-[11px] mt-1 leading-relaxed">
                Keep playing Ranked to improve your Season Rank.
              </div>
            </div>
          )}
        </div>
      </SectionShell>
    );
  }

  // Match #16+ — did NOT count. Explicit, non-misleading messaging.
  return (
    <SectionShell title="Weekly Cup" testid="weekly-section-not-counted">
      <div className="flex flex-col items-center">
        <div
          data-testid="weekly-progress"
          className="font-mono text-white/80 text-lg tracking-[0.15em]"
        >
          {countedMatches} / {max} <span className="text-white/50">Complete</span>
        </div>
        <div
          data-testid="weekly-not-counted"
          className="font-heading text-[10px] tracking-[0.32em] uppercase text-white/55 mt-2 text-center leading-relaxed"
        >
          This match did not count
        </div>
      </div>
    </SectionShell>
  );
}

// ---------------------------------------------------------------------------
function SectionShell({ title, testid, children }) {
  return (
    <div
      data-testid={testid}
      className="px-5 py-4 flex flex-col items-center"
    >
      <div className="font-heading text-[10px] tracking-[0.32em] uppercase text-white/50 mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}
